import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSubsidyAllocationStore, EUCountry } from "../store/subsidyAllocationStore";
import { useAIContext } from "../store/aiContextStore";
import { useSettlementLedger } from "../store/settlementLedger";
import { useKeyboardShortcuts } from "../hooks/useKeyboardShortcuts";

/**
 * EU Cross-Sovereign Subsidy Allocation (v2-v3)
 * 
 * Country-level subsidy allocation management:
 * - Plafons per country
 * - Over-exposure detection
 * - Fraud risk scoring
 * - ESG compliance conditions
 * - Election-year sensitivity
 * 
 * Integrates with Settlement Ledger for CO₂/ESG credit financial calculations.
 */
export default function SubsidyAllocation() {
  const { t } = useTranslation();
  const store = useSubsidyAllocationStore();
  const ledger = useSettlementLedger();
  
  const [selectedCountry, setSelectedCountry] = useState<EUCountry>("HU");
  const [allocationAmount, setAllocationAmount] = useState<number>(0);
  const [fraudRiskScore, setFraudRiskScore] = useState<number>(0);
  const [eligibilityScore, setEligibilityScore] = useState<number>(100);
  const [esgCompliant, setEsgCompliant] = useState<boolean>(true);
  const [electionSensitivity, setElectionSensitivity] = useState<number>(0);

  // Set active panel
  useEffect(() => {
    useAIContext.getState().setActivePanel("SubsidyAllocationPanel");
  }, []);

  // Update AI context
  useEffect(() => {
    const allocations = store.countryAllocations;
    useAIContext.getState().setInput("subsidyAllocations", allocations);
    useAIContext.getState().setInput("totalAllocated", store.getTotalAllocated());
    useAIContext.getState().setInput("overExposedCountries", store.getOverExposedCountries());
    useAIContext.getState().setInput("highFraudRiskCountries", store.getHighFraudRiskCountries());
  }, [store.countryAllocations, store.getTotalAllocated(), store.getOverExposedCountries(), store.getHighFraudRiskCountries]);

  const selectedAllocation = store.countryAllocations[selectedCountry];
  const utilization = store.getCountryUtilization(selectedCountry);
  const overExposed = store.getOverExposedCountries();
  const highFraudRisk = store.getHighFraudRiskCountries();

  const sortedCountries = useMemo(() => {
    return (Object.keys(store.countryAllocations) as EUCountry[]).sort((a, b) => {
      const allocA = store.countryAllocations[a];
      const allocB = store.countryAllocations[b];
      return allocB.allocated - allocA.allocated;
    });
  }, [store.countryAllocations]);

  const handleAllocate = async () => {
    if (allocationAmount <= 0) return;
    await store.allocateSubsidy(selectedCountry, allocationAmount);
    
    // Also record in Settlement Ledger as SUBSIDY scope transfer
    ledger.recordTransfer(
      "EU_FUND",
      selectedCountry,
      allocationAmount,
      `SUBSIDY_ALLOCATION:${selectedCountry}`,
      "SUBSIDY"
    );
    
    setAllocationAmount(0);
  };

  const handleUpdateFraudRisk = () => {
    store.setFraudRiskScore(selectedCountry, fraudRiskScore);
  };

  const handleUpdateEligibility = () => {
    store.setEligibilityScore(selectedCountry, eligibilityScore);
  };

  const handleUpdateESGCompliance = () => {
    store.setESGCompliance(selectedCountry, esgCompliant);
  };

  const handleUpdateElectionSensitivity = () => {
    store.setElectionYearSensitivity(selectedCountry, electionSensitivity);
  };

  // Keyboard shortcuts (after function definitions)
  useKeyboardShortcuts([
    { key: 'e', ctrl: true, action: () => store.exportAllocationReport(), description: 'Export allocation report' },
    { key: 'a', ctrl: true, action: handleAllocate, description: 'Allocate subsidy' },
  ]);

  return (
    <div className="space-y-6 font-orbitron bg-black text-neutral-100 p-6">
      <div className="flex items-center justify-between border-b border-evat-gold/40 pb-2">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-wide text-evat-neon">
            EU Cross-Sovereign Subsidy Allocation
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Országonkénti támogatási plafon, over-exposure, fraud risk, ESG compliance, election-year sensitivity
          </p>
        </div>
        <button
          onClick={store.exportAllocationReport}
          className="px-4 py-2 bg-evat-neon/20 border border-evat-neon text-evat-neon rounded hover:bg-evat-neon/30 transition-colors text-sm font-orbitron"
        >
          Export Report
        </button>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="rounded-xl border border-evat-gold/60 bg-black/40 p-4">
          <div className="text-xs uppercase text-gray-400">Total EU Plafon</div>
          <div className="text-2xl font-semibold mt-1 text-evat-neon">
            €{(store.totalEUPlafon / 1000000).toFixed(0)}M
          </div>
        </div>
        <div className="rounded-xl border border-evat-gold/60 bg-black/40 p-4">
          <div className="text-xs uppercase text-gray-400">Total Allocated</div>
          <div className="text-2xl font-semibold mt-1 text-evat-neon">
            €{(store.getTotalAllocated() / 1000000).toFixed(1)}M
          </div>
        </div>
        <div className="rounded-xl border border-evat-gold/60 bg-black/40 p-4">
          <div className="text-xs uppercase text-gray-400">Remaining Budget</div>
          <div className="text-2xl font-semibold mt-1 text-evat-neon">
            €{(store.getRemainingBudget() / 1000000).toFixed(1)}M
          </div>
        </div>
        <div className="rounded-xl border border-evat-gold/60 bg-black/40 p-4">
          <div className="text-xs uppercase text-gray-400">Over-Exposed</div>
          <div className="text-2xl font-semibold mt-1 text-red-400">
            {overExposed.length}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Country selection and allocation */}
        <div className="rounded-xl border border-evat-gold/40 bg-black/40 p-4 space-y-4">
          <h2 className="font-semibold text-evat-neon">Country Allocation</h2>
          
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Select Country</label>
            <select
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value as EUCountry)}
              className="w-full px-3 py-2 rounded bg-black/60 border border-evat-gold/40 text-evat-neon text-sm font-orbitron"
            >
              {(Object.keys(store.countryAllocations) as EUCountry[]).map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>
          </div>

          <div className="p-3 border border-evat-gold/30 rounded bg-black/30">
            <div className="text-xs text-gray-400 mb-2">Current Status</div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-gray-400">Plafon:</span>
                <span className="ml-2 text-evat-neon">€{(selectedAllocation.plafon / 1000000).toFixed(1)}M</span>
              </div>
              <div>
                <span className="text-gray-400">Allocated:</span>
                <span className={`ml-2 ${selectedAllocation.overExposure ? "text-red-400" : "text-evat-neon"}`}>
                  €{(selectedAllocation.allocated / 1000000).toFixed(2)}M
                </span>
              </div>
              <div>
                <span className="text-gray-400">Utilization:</span>
                <span className={`ml-2 ${utilization >= 90 ? "text-red-400" : utilization >= 70 ? "text-yellow-400" : "text-evat-neon"}`}>
                  {utilization.toFixed(1)}%
                </span>
              </div>
              <div>
                <span className="text-gray-400">Eligibility:</span>
                <span className={`ml-2 ${selectedAllocation.eligibilityScore >= 70 ? "text-evat-neon" : "text-yellow-400"}`}>
                  {selectedAllocation.eligibilityScore}%
                </span>
              </div>
            </div>
            {selectedAllocation.overExposure && (
              <div className="mt-2 text-xs text-red-400">
                ⚠️ Over-exposure detected (&gt;90% of plafon)
              </div>
            )}
            {selectedAllocation.fraudRiskScore >= store.fraudRiskThreshold && (
              <div className="mt-2 text-xs text-red-400">
                ⚠️ High fraud risk ({selectedAllocation.fraudRiskScore}%)
              </div>
            )}
            {!selectedAllocation.esgCompliance && (
              <div className="mt-2 text-xs text-yellow-400">
                ⚠️ ESG compliance issue
              </div>
            )}
            {selectedAllocation.electionYearSensitivity > 0 && (
              <div className="mt-2 text-xs text-yellow-400">
                ⚠️ Election year sensitivity: {selectedAllocation.electionYearSensitivity}/5
              </div>
            )}
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-2 block">Allocation Amount (EUR)</label>
            <input
              type="number"
              value={allocationAmount || ""}
              onChange={(e) => setAllocationAmount(Number(e.target.value))}
              className="w-full px-3 py-2 rounded bg-black/60 border border-evat-gold/40 text-evat-neon text-sm font-orbitron"
              placeholder="Enter amount"
            />
          </div>

          <button
            onClick={handleAllocate}
            disabled={allocationAmount <= 0 || selectedAllocation.overExposure}
            className="w-full px-4 py-2 rounded bg-evat-neon/20 border border-evat-neon text-evat-neon hover:bg-evat-neon/30 transition-colors text-sm font-semibold font-orbitron disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Allocate Subsidy
          </button>
        </div>

        {/* Risk and compliance management */}
        <div className="rounded-xl border border-evat-gold/40 bg-black/40 p-4 space-y-4">
          <h2 className="font-semibold text-evat-neon">Risk & Compliance</h2>
          
          <div>
            <label className="text-sm text-gray-400 mb-2 block">
              Fraud Risk Score (0-100)
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                min="0"
                max="100"
                value={fraudRiskScore}
                onChange={(e) => setFraudRiskScore(Number(e.target.value))}
                className="flex-1 px-3 py-2 rounded bg-black/60 border border-evat-gold/40 text-evat-neon text-sm font-orbitron"
              />
              <button
                onClick={handleUpdateFraudRisk}
                className="px-4 py-2 rounded bg-evat-gold/20 border border-evat-gold text-evat-gold hover:bg-evat-gold/30 transition-colors text-sm font-orbitron"
              >
                Update
              </button>
            </div>
            <div className="mt-1 text-xs text-gray-500">
              Current: {selectedAllocation.fraudRiskScore}%
            </div>
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-2 block">
              Eligibility Score (0-100)
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                min="0"
                max="100"
                value={eligibilityScore}
                onChange={(e) => setEligibilityScore(Number(e.target.value))}
                className="flex-1 px-3 py-2 rounded bg-black/60 border border-evat-gold/40 text-evat-neon text-sm font-orbitron"
              />
              <button
                onClick={handleUpdateEligibility}
                className="px-4 py-2 rounded bg-evat-gold/20 border border-evat-gold text-evat-gold hover:bg-evat-gold/30 transition-colors text-sm font-orbitron"
              >
                Update
              </button>
            </div>
            <div className="mt-1 text-xs text-gray-500">
              Current: {selectedAllocation.eligibilityScore}%
            </div>
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-2 block">ESG Compliance</label>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={esgCompliant}
                onChange={(e) => setEsgCompliant(e.target.checked)}
                className="w-4 h-4 rounded border-evat-gold/40 bg-black/60 text-evat-neon"
              />
              <span className="text-sm text-gray-300">Compliant</span>
              <button
                onClick={handleUpdateESGCompliance}
                className="ml-auto px-4 py-2 rounded bg-evat-gold/20 border border-evat-gold text-evat-gold hover:bg-evat-gold/30 transition-colors text-sm font-orbitron"
              >
                Update
              </button>
            </div>
            <div className="mt-1 text-xs text-gray-500">
              Current: {selectedAllocation.esgCompliance ? "Compliant" : "Non-compliant"}
            </div>
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-2 block">
              Election Year Sensitivity (0-5)
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                min="0"
                max="5"
                value={electionSensitivity}
                onChange={(e) => setElectionSensitivity(Number(e.target.value))}
                className="flex-1 px-3 py-2 rounded bg-black/60 border border-evat-gold/40 text-evat-neon text-sm font-orbitron"
              />
              <button
                onClick={handleUpdateElectionSensitivity}
                className="px-4 py-2 rounded bg-evat-gold/20 border border-evat-gold text-evat-gold hover:bg-evat-gold/30 transition-colors text-sm font-orbitron"
              >
                Update
              </button>
            </div>
            <div className="mt-1 text-xs text-gray-500">
              Current: {selectedAllocation.electionYearSensitivity}/5
              {selectedAllocation.electionYearSensitivity >= 3 && (
                <span className="ml-2 text-yellow-400">⚠️ High sensitivity</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Countries table */}
      <div className="rounded-xl border border-evat-gold/40 bg-black/40 p-4">
        <h2 className="font-semibold text-evat-neon mb-3">All Countries Overview</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-400 border-b border-evat-gold/40">
              <tr>
                <th className="text-left py-2 pr-2">Country</th>
                <th className="text-right py-2 px-2">Plafon</th>
                <th className="text-right py-2 px-2">Allocated</th>
                <th className="text-right py-2 px-2">Utilization</th>
                <th className="text-right py-2 px-2">Eligibility</th>
                <th className="text-right py-2 px-2">Fraud Risk</th>
                <th className="text-center py-2 px-2">ESG</th>
                <th className="text-center py-2 px-2">Election</th>
                <th className="text-center py-2 pl-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {sortedCountries.map((country) => {
                const alloc = store.countryAllocations[country];
                const util = store.getCountryUtilization(country);
                return (
                  <tr
                    key={country}
                    className="border-b border-white/5 hover:bg-black/30 cursor-pointer"
                    onClick={() => setSelectedCountry(country)}
                  >
                    <td className="py-1 pr-2 font-mono text-xs md:text-sm text-evat-neon">
                      {country}
                    </td>
                    <td className="py-1 px-2 text-right text-gray-300">
                      €{(alloc.plafon / 1000000).toFixed(1)}M
                    </td>
                    <td className="py-1 px-2 text-right text-gray-300">
                      €{(alloc.allocated / 1000000).toFixed(2)}M
                    </td>
                    <td className={`py-1 px-2 text-right font-orbitron ${
                      util >= 90 ? "text-red-400" : util >= 70 ? "text-yellow-400" : "text-evat-neon"
                    }`}>
                      {util.toFixed(1)}%
                    </td>
                    <td className={`py-1 px-2 text-right ${
                      alloc.eligibilityScore >= 70 ? "text-evat-neon" : "text-yellow-400"
                    }`}>
                      {alloc.eligibilityScore}%
                    </td>
                    <td className={`py-1 px-2 text-right ${
                      alloc.fraudRiskScore >= store.fraudRiskThreshold ? "text-red-400" : "text-gray-300"
                    }`}>
                      {alloc.fraudRiskScore}%
                    </td>
                    <td className="py-1 px-2 text-center">
                      {alloc.esgCompliance ? (
                        <span className="text-evat-neon">✓</span>
                      ) : (
                        <span className="text-red-400">✗</span>
                      )}
                    </td>
                    <td className="py-1 px-2 text-center">
                      {alloc.electionYearSensitivity > 0 ? (
                        <span className={`text-xs ${
                          alloc.electionYearSensitivity >= 3 ? "text-red-400" : "text-yellow-400"
                        }`}>
                          {alloc.electionYearSensitivity}/5
                        </span>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </td>
                    <td className="py-1 pl-2 text-center">
                      {alloc.overExposure && (
                        <span className="text-xs text-red-400">⚠️ Over</span>
                      )}
                      {alloc.fraudRiskScore >= store.fraudRiskThreshold && (
                        <span className="text-xs text-red-400 ml-1">🔴 Risk</span>
                      )}
                      {!alloc.esgCompliance && (
                        <span className="text-xs text-yellow-400 ml-1">⚠️ ESG</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

